import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
// ── FIREBASE AUTHENTICATION IS GONE FROM THIS SCREEN ──
// Login is a single call to our own backend against the MongoDB password, and
// "Forgot password?" now opens /forgot-password (Resend OTP) instead of asking
// Firebase to email a reset link.
//
// Firebase is NOT gone from the app: registerFcmToken() below is Firebase Cloud
// Messaging, which is untouched by this migration — only the AUTH use was
// removed.
import { registerFcmToken } from "../services/fcm";
import InstallButton from "./InstallButton";
// SECURITY (10.1): stable client device fingerprint sent with login.
import { getDeviceFingerprint } from "../utils/deviceFingerprint";
import "./Login.css";
import BannerAds from "./BannerAds";

function Login() {

  const navigate = useNavigate();

  // REFER & EARN — the referral code this visitor arrived with, if any.
  // A referral link can point at EITHER page: someone who already has an
  // account lands here rather than on Register. We capture the code the same
  // way Register.jsx does (same localStorage key), so whichever page they open
  // first, and whichever they finish on, the code survives.
  const [refCode, setRefCode] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [voices, setVoices] = useState([]);

  // FORGOT PASSWORD is a full screen of its own now (/forgot-password): it is
  // three steps — email, code, new password + confirm — which is more than a
  // one-field modal can carry. The modal that used to live here, and the
  // Firebase reset-link call behind it, are gone.

  /* =========================
     REFER & EARN — CAPTURE THE CODE (STEP 3 / STEP 4)
     Reads ?ref= from the URL and remembers it, exactly as Register.jsx does
     and under the same key. A visitor who arrives on a referral link but
     already has an account logs in here; the code is applied to their account
     right after a successful login below.
  ========================= */

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref && ref.trim()) {
      const code = ref.trim();
      setRefCode(code);
      localStorage.setItem("referralCode", code);
    } else {
      const stored = localStorage.getItem("referralCode");
      if (stored) setRefCode(stored);
    }
  }, []);

  /* =========================
     CANVAS PARTICLES
  ========================= */

  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const animRef = useRef(null);

  const COLORS = [
    "#6332ff",
    "#00ffc8",
    "#f472b6",
    "#60a5fa",
    "#a78bfa",
  ];

  const makeParticle = useCallback(
    (W, H) => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.8 + 0.4,
      color:
        COLORS[
          Math.floor(
            Math.random() * COLORS.length
          )
        ],
      alpha: Math.random() * 0.6 + 0.2,
      pulse: Math.random() * Math.PI * 2,
    }),
    []
  );

  useEffect(() => {

    const canvas = canvasRef.current;
    const root = rootRef.current;

    if (!canvas ||!root) return;

    const ctx = canvas.getContext("2d");

    let W = root.offsetWidth;
    let H = root.offsetHeight;

    let particles = [];

    const resize = () => {

      W = root.offsetWidth;
      H = root.offsetHeight;

      canvas.width = W;
      canvas.height = H;
    };

    resize();

    particles = Array.from(
      { length: 80 },
      () => makeParticle(W, H)
    );

    const draw = () => {

      ctx.clearRect(0, 0, W, H);

      particles.forEach((p) => {

        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.018;

        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;

        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;

        const pa =
          p.alpha *
          (0.7 + 0.3 * Math.sin(p.pulse));

        ctx.beginPath();

        ctx.arc(
          p.x,
          p.y,
          p.r,
          0,
          Math.PI * 2
        );

        ctx.fillStyle = p.color;
        ctx.globalAlpha = pa;
        ctx.fill();
      });

      ctx.globalAlpha = 1;

      for (
        let i = 0;
        i < particles.length;
        i++
      ) {

        for (
          let j = i + 1;
          j < particles.length;
          j++
        ) {

          const dx =
            particles[i].x - particles[j].x;

          const dy =
            particles[i].y - particles[j].y;

          const dist = Math.sqrt(
            dx * dx + dy * dy
          );

          if (dist < 100) {

            ctx.beginPath();

            ctx.moveTo(
              particles[i].x,
              particles[i].y
            );

            ctx.lineTo(
              particles[j].x,
              particles[j].y
            );

            ctx.strokeStyle =
              particles[i].color;

            ctx.lineWidth = 0.5;

            ctx.globalAlpha =
              (1 - dist / 100) * 0.12;

            ctx.stroke();
          }
        }
      }

      ctx.globalAlpha = 1;

      animRef.current =
        requestAnimationFrame(draw);
    };

    draw();

    const ro = new ResizeObserver(
      resize
    );

    ro.observe(root);

    window.addEventListener(
      "resize",
      resize
    );

    return () => {

      cancelAnimationFrame(
        animRef.current
      );

      ro.disconnect();

      window.removeEventListener(
        "resize",
        resize
      );
    };

  }, [makeParticle]);

  /* =========================
     BASE URL
  ========================= */

  const BASE_URL =
    window.location.hostname ===
    "localhost"
   ? "http://localhost:5000"
      : "https://success-blog-app-backend.onrender.com";

  /* =========================
     TOAST
  ========================= */

  const showToast = (
    message,
    type = "info"
  ) => {

    const toast =
      document.createElement("div");

    toast.className =
      `rg-toast rg-toast-${type}`;

    toast.innerText = message;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 3000);
  };


    /* =========================
     LOAD VOICES
  ========================= */

  useEffect(() => {

    const loadVoices = () => {

      // SAFETY: Some browsers / Instagram WebView
      // may not provide speechSynthesis.
      if (
        !window.speechSynthesis ||
        typeof window.speechSynthesis.getVoices !== "function"
      ) {
        setVoices([]);
        return;
      }

      try {

        const availableVoices =
          window.speechSynthesis.getVoices();

        setVoices(availableVoices);

      } catch (error) {

        console.warn(
          "Speech voices unavailable:",
          error
        );

        setVoices([]);

      }
    };

    // SAFETY: Prevent "Cannot set properties of undefined
    // (setting 'onvoiceschanged')" in Instagram WebView.
    if (
      window.speechSynthesis &&
      "onvoiceschanged" in window.speechSynthesis
    ) {

      window.speechSynthesis.onvoiceschanged =
        loadVoices;

    }

    loadVoices();

    // Cleanup
    return () => {

      if (
        window.speechSynthesis &&
        "onvoiceschanged" in window.speechSynthesis
      ) {

        window.speechSynthesis.onvoiceschanged =
          null;

      }

    };

  }, []);

  /* =========================
     LOGIN — ONE CALL, OUR OWN BACKEND
     There is no Firebase sign-in step any more, and no client-side
     "emailVerified" check either. BOTH verification gates (email and face) are
     decided by the server: it refuses to issue a JWT until they pass, and
     answers with a `reason` this screen routes on. That is the difference that
     matters — the old check could be skipped by anyone calling the API
     directly, and this one cannot be.
  ========================= */

  const handleLogin = async (e) => {

    e.preventDefault();

    setLoading(true);

    try {

      // SECURITY (10.1): device fingerprint sent with login (additive fields).
      const fp = await getDeviceFingerprint();

      const res = await axios.post(
        `${BASE_URL}/api/auth/login`,
        {
          email,
          password,
          deviceHash: fp.deviceHash,
          clientFingerprint: fp.signals,
        }
      );

      const username =
        res.data.user.name;

      localStorage.setItem(
        "token",
        res.data.token
      );

      localStorage.setItem(
        "username",
        username
      );

      localStorage.setItem(
        "userId",
        res.data.user.id
      );

      localStorage.setItem(
        "isAdmin",
        res.data.user.isAdmin
       ? "true"
          : "false"
      );

      showToast(
        "Login Successful ✅",
        "success"
      );

      // 🔔 Register this device for push notifications now that we are
      // authenticated (the token is saved via the JWT just stored above).
      // Fire-and-forget: it never throws and must not delay the redirect.
      registerFcmToken();

      /* =========================
         REFER & EARN — NEW USERS ONLY
         Logging in does NOT apply a referral code. Only a new account created
         through the referral link counts as a referred user, so nothing is
         claimed here.

         The stored code is deliberately LEFT IN PLACE: someone who opened the
         link, landed on this page and then goes to Register still carries their
         code across, because Register reads the same key.
      ========================= */

      // Voice
      // SAFETY: Voice is optional. If Instagram WebView/browser
      // does not support Speech Synthesis, login continues normally.

      if (
        "speechSynthesis" in window &&
        typeof window.SpeechSynthesisUtterance !== "undefined"
      ) {

        try {

          const msg =
            new SpeechSynthesisUtterance(
              `Welcome ${username}`
            );

          msg.voice =
            voices.find((v) =>
              v.name
             .toLowerCase()
             .includes("female")
            ) || voices[0] || null;

          msg.lang = "en-US";

          window.speechSynthesis.speak(msg);

        } catch (voiceError) {

          console.warn(
            "Voice greeting unavailable:",
            voiceError
          );

          // IMPORTANT:
          // Do not stop login if voice fails.
        }

      }
      
      /* =========================
         REDIRECT AFTER LOGIN
      ========================= */

      const redirectPath =
        localStorage.getItem(
          "redirectAfterLogin"
        );

      if (redirectPath) {

        localStorage.removeItem(
          "redirectAfterLogin"
        );

        navigate(redirectPath);

      } else {

        navigate("/home");
      }

    } catch (err) {

      /* =========================
         EMAIL VERIFICATION GATE  (Part 5)
         The account exists and the password was right, but the email check is
         still outstanding, so the server refused to issue a token. Send them to
         the OTP screen rather than showing a dead-end error — this is the way
         back into the flow for anyone who closed the tab before entering their
         code, which is what makes "leave and return later" work here too.

         The email comes from the server's reply, so it is the address the
         account actually holds rather than whatever case was typed here.

         No loop is possible: this page only redirects after a real login
         attempt returns this reason, and the OTP screen's links come back here
         without attempting one.
      ========================= */
      if (err.response?.data?.reason === "email_verification_required") {

        const pendingEmail =
          err.response.data.email ||
          email.trim().toLowerCase();

        showToast(
          "One quick step left — verify your email",
          "info"
        );

        navigate(
          "/verify-otp",
          { state: { email: pendingEmail } }
        );

        setLoading(false);

        return;
      }

      /* =========================
         FACE VERIFICATION GATE
         The account exists and the password was right, but the face check
         registration promised has not been done yet, so the server refused to
         issue a token. Send them to finish it rather than showing a dead-end
         error — this is the ONLY way back into the flow for someone who left
         the face page earlier, which is what makes "leave and return later"
         work. The email comes from the server's reply so it is the address the
         account actually holds, not whatever case was typed here.

         No loop is possible: this page only redirects after a real login
         attempt returns this reason, and the face page's Back button returns
         here without attempting one.
      ========================= */
      if (err.response?.data?.reason === "face_verification_required") {

        const pendingEmail =
          err.response.data.email ||
          email.trim().toLowerCase();

        showToast(
          "One quick step left — face verification",
          "info"
        );

        navigate(
          "/face-verification",
          { state: { email: pendingEmail } }
        );

        setLoading(false);

        return;
      }

      showToast(
        err.response?.data?.message ||
          err.message ||
          "Login Failed ❌",
        "error"
      );

    } finally {

      setLoading(false);
    }
  };

  return (

    <div
      className="rg-root"
      ref={rootRef}
    >

      <canvas
        className="rg-canvas"
        ref={canvasRef}
      />

      <div className="rg-aurora" />

      <div className="rg-card">

        <div className="rg-card-scanline" />

        <div className="rg-brand-label">
          ✦ TheBlogsSphere
        </div>

        <h2 className="rg-title">
          Welcome Back 👋
        </h2>

        <p className="rg-subtitle">
          login to continue
        </p>

        {/* REFER & EARN — no referral content on this page. The code arriving on
            the link is still captured above and still applied at registration;
            the steps are explained on the Refer & Earn page only. */}

        <form
          onSubmit={handleLogin}
          className="rg-form"
        >

          <div className="rg-field-group">

            <label className="rg-label">
              Email
            </label>

            <input
              className="rg-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) =>
                setEmail(e.target.value)
              }
              required
            />

          </div>

          <div className="rg-field-group">

            <label className="rg-label">
              Password
            </label>

            <div className="rg-password-wrap">

              <input
                className="rg-input"
                type={
                  showPassword
                 ? "text"
                    : "password"
                }
                placeholder="Enter password"
                value={password}
                onChange={(e) =>
                  setPassword(
                    e.target.value
                  )
                }
                required
              />

              <span
                className="rg-eye"
                onClick={() =>
                  setShowPassword(
                 !showPassword
                  )
                }
              >
                {showPassword
               ? "🙈"
                  : "👁"}
              </span>

            </div>

          </div>

          {/* FORGOT PASSWORD — its own screen now (email → code → new
              password → confirm), not a modal that emails a Firebase link. */}
          <div style={{ textAlign: 'right', marginTop: '-10px', marginBottom: '15px' }}>
            <Link
              to="/forgot-password"
              className="rg-link"
              style={{ fontSize: '14px' }}
            >
              Forgot Password?
            </Link>
          </div>

          <button
            className="rg-submit"
            disabled={loading}
          >

            {loading? (

              <span className="rg-spinner-row">

                <span className="rg-spinner" />

                Logging in...

              </span>

            ) : (
              "Login →"
            )}

          </button>

        </form>

        <div className="rg-divider" />
        <BannerAds/>
        <p className="rg-footer-text">

          Don’t have an account?{" "}

          <Link
            to="/register"
            className="rg-link"
          >
            Register
          </Link>

        </p>

      </div>

      <InstallButton />

    </div>
  );
}

export default Login;
